import {
  ParentLimitedResponse,
  StudentLimitedResponse,
  TutorLimitedResponse,
} from "client/openapi";
import { AttentionIssue } from "client/openapi/models/AttentionIssue";
import { OnboardingStep } from "client/openapi/models/OnboardingStep";
import { PeopleTab } from "client/openapi/models/PeopleTab";
import type { PeopleRecipientsResponse } from "client/openapi/models/PeopleRecipientsResponse";
import { PeopleService } from "client/openapi/services/PeopleService";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import { Dialog } from "components/Dialog";
import { ComponentProps, useState } from "react";
import Notifications from "util/notifications";

type Recipients = Omit<ComponentProps<typeof ComposeMessageDialog>, "org_id">;

/** A kind with nobody stays undefined: an empty list would still show an empty picker. */
function toRecipients({
  students,
  parents,
  tutors,
}: PeopleRecipientsResponse): Recipients {
  return {
    students: students.length
      ? (students as StudentLimitedResponse[])
      : undefined,
    parents: parents.length ? (parents as ParentLimitedResponse[]) : undefined,
    tutors: tutors.length ? (tutors as TutorLimitedResponse[]) : undefined,
  };
}

/**
 * Messages exactly the people on the tab: a whole kind (or everyone) by the
 * dialog's org-wide flags, Needs attention or a search by the server's list.
 */
export default function MessageAll({
  orgId,
  tab,
  q,
  missing = [],
  issues = [],
}: {
  orgId: number;
  tab: PeopleTab;
  q?: string;
  /** The Onboarding tab's Missing: filter. */
  missing?: OnboardingStep[];
  /** The Needs attention tab's "Show people with:" filter. */
  issues?: AttentionIssue[];
}) {
  const [recipients, setRecipients] = useState<Recipients | null>(null);

  function open() {
    if (!q && tab !== PeopleTab.ATTENTION && tab !== PeopleTab.ONBOARDING) {
      const all = tab === PeopleTab.ALL;
      setRecipients({
        messageAllStudents: all || tab === PeopleTab.STUDENT,
        messageAllParents: all || tab === PeopleTab.PARENT,
        messageAllTutors: all || tab === PeopleTab.TUTOR,
      });
      return;
    }
    PeopleService.getPeopleRecipients({
      orgId,
      requestBody: { q, tab, missing, issues },
    })
      .then((people) => setRecipients(toRecipients(people)))
      .catch(() =>
        Notifications.error("Could not load the people to message.")
      );
  }

  return (
    <Dialog
      open={recipients !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setRecipients(null);
      }}
    >
      <Button
        size={ButtonSize.SMALL}
        color={ButtonColor.PURPLE}
        fill={ButtonFill.HOLLOW}
        onClick={open}
      >
        Message all
      </Button>

      {/* The dialog copies its recipients when it mounts, so it mounts only while open. */}
      {recipients && <ComposeMessageDialog org_id={orgId} {...recipients} />}
    </Dialog>
  );
}
